import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { formatINR } from "../lib/utils-format";
import {
    ArrowDownLeft, ArrowDownRight, Wallet, TrendingUp,
    UserPlus, AlertCircle, Clock, FileCheck, Copy, CheckCircle2,
    ChevronRight, Search, Users, Zap, IndianRupee, Award, Layers,
} from "lucide-react";

const TYPE_BADGE = {
    direct_bonus_credit:    { label: "Direct Referral",  cls: "bg-[#10B1B6]/10 text-[#10B1B6]"  },
    pairing_bonus_credit:   { label: "Pairing Bonus",    cls: "bg-[#7059FD]/10 text-[#7059FD]"  },
    travel_fund_credit:     { label: "Travel Fund (5%)", cls: "bg-blue-100 text-blue-700"        },
    admin_charge_deduction: { label: "Admin Charges",    cls: "bg-orange-100 text-orange-700"    },
    tds_deduction:          { label: "TDS",              cls: "bg-red-100 text-red-700"          },
    withdrawal_debit:       { label: "Withdrawal",       cls: "bg-amber-100 text-amber-700"      },
    adjustment:             { label: "Adjustment",       cls: "bg-slate-100 text-slate-600"      },
};

const BONUS_TYPES = new Set(["direct_bonus_credit", "pairing_bonus_credit"]);

function getBonusBreakdown(entry) {
    if (!BONUS_TYPES.has(entry.type)) return null;
    const m = entry.meta;
    if (m?.admin_charge != null) {
        return { gross: m.gross, admin: m.admin_charge, tds: m.tds, fund: m.travel_fund };
    }
    const gross = entry.amount / 0.75;
    return { gross, admin: gross * 0.15, tds: gross * 0.05, fund: gross * 0.05, estimated: true };
}

export default function Dashboard() {
    const { user } = useAuth();
    const [balance,      setBalance]      = useState(null);
    const [ledger,       setLedger]       = useState([]);
    const [preview,      setPreview]      = useState(null);
    const [directIncome, setDirectIncome] = useState([]);
    const [teamIncome,   setTeamIncome]   = useState(null);
    const [copied,       setCopied]       = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [b, l, p, d, t] = await Promise.all([
                    api.get("/wallet/balance"),
                    api.get("/wallet/ledger", { params: { limit: 10 } }),
                    api.get("/pairing/preview"),
                    api.get("/wallet/direct-income"),
                    api.get("/wallet/team-income"),
                ]);
                setBalance(b.data);
                setLedger(l.data);
                setPreview(p.data);
                setDirectIncome(d.data);
                setTeamIncome(t.data);
            } catch (_) {}
        })();
    }, []);

    const kycApproved = user?.kyc_status === "approved";
    const kycPending  = user?.kyc_status === "pending";
    const leftCount   = user?.left_active_count  ?? 0;
    const rightCount  = user?.right_active_count ?? 0;
    const maxPairs    = Math.min(leftCount, rightCount);
    const symmetryPct = Math.max(leftCount, rightCount) > 0
        ? Math.round((Math.min(leftCount, rightCount) / Math.max(leftCount, rightCount)) * 100)
        : 0;

    const directTotalNet  = directIncome.reduce((a, e) => a + e.amount, 0);
    const directTotalGross= directIncome.reduce((a, e) => a + (getBonusBreakdown(e)?.gross ?? 0), 0);
    const directPending   = directIncome.filter(e => !e.settled).length;
    const directSettled   = directIncome.filter(e => e.settled).length;

    const copyId = () => {
        if (!user?.referral_code) return;
        navigator.clipboard.writeText(user.referral_code).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <AppShell>
            <div className="flex gap-6 items-start">

                {/* ─────────────── Main content ─────────────── */}
                <div className="flex-1 min-w-0 space-y-6">

                    {/* ── Header ── */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <div className="overline">Dashboard</div>
                            <div className="flex items-center gap-3 flex-wrap mt-1">
                                <h1 className="font-display font-black text-3xl tracking-tighter text-[#1B2B6B]">
                                    Hello, {user?.full_name?.split(" ")[0] || "there"}
                                </h1>
                                <div className="flex items-center gap-2 flex-wrap">
                                    {user?.status === "active" && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                                            <span className="w-1.5 h-1.5 mr-1.5 bg-emerald-500 rounded-full animate-pulse" />
                                            active
                                        </span>
                                    )}
                                    {kycApproved && (
                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-[#2563EB] border border-blue-200">
                                            <FileCheck className="w-3 h-3" /> KYC Verified
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="relative w-full sm:w-64">
                            <input
                                type="text"
                                placeholder="Search activities..."
                                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-full text-sm focus:outline-none focus:border-[#2563EB] shadow-sm transition-colors"
                            />
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        </div>
                    </div>

                    {/* ── KYC banners ── */}
                    {!kycApproved && !kycPending && (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex flex-wrap items-start gap-4" data-testid="kyc-banner">
                            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-[200px]">
                                <div className="font-semibold text-amber-900">KYC verification required</div>
                                <div className="text-sm text-amber-800 mt-1">
                                    Submit your Aadhaar and PAN to unlock pairing bonuses and withdrawals.
                                </div>
                            </div>
                            <Link to="/app/kyc" className="px-5 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm transition-colors">
                                Complete KYC
                            </Link>
                        </div>
                    )}
                    {kycPending && (
                        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5 flex items-center gap-4">
                            <Clock className="w-5 h-5 text-blue-600 shrink-0" />
                            <div className="text-sm text-blue-800">
                                <span className="font-semibold">KYC under review.</span> Our team will verify your documents within 24–48 hours.
                            </div>
                        </div>
                    )}

                    {/* ── Member ID Hero card ── */}
                    <div className="relative overflow-hidden bg-gradient-to-r from-[#1B2B6B] to-[#2A3D82] text-white rounded-3xl p-6 sm:p-8 shadow-lg">
                        <div className="absolute right-0 top-0 translate-x-12 -translate-y-12 opacity-10 pointer-events-none select-none">
                            <svg className="w-80 h-80 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                            </svg>
                        </div>
                        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                            <div className="space-y-2">
                                <span className="text-xs font-bold tracking-widest text-blue-300 uppercase">Your Member ID</span>
                                <div className="flex items-center gap-3">
                                    <span
                                        className="text-3xl sm:text-4xl font-extrabold text-[#f59e0b] tracking-wider"
                                        style={{ textShadow: "0 0 12px rgba(245,158,11,0.35)" }}
                                    >
                                        {user?.referral_code}
                                    </span>
                                    <button
                                        onClick={copyId}
                                        className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-slate-200 transition-all hover:scale-105"
                                        title="Copy ID"
                                    >
                                        {copied
                                            ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                            : <Copy className="w-5 h-5" />
                                        }
                                    </button>
                                </div>
                                <p className="text-xs text-slate-300 max-w-md">
                                    Share this sponsor ID with your prospects to automatically link new partners directly to your network.
                                </p>
                            </div>
                            <Link
                                to="/app/add-member"
                                className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#f59e0b] hover:bg-[#d97706] active:scale-95 text-[#1B2B6B] font-bold rounded-2xl shadow-lg transition-all whitespace-nowrap"
                            >
                                <UserPlus className="w-5 h-5" />
                                Add New Member
                            </Link>
                        </div>
                    </div>

                    {/* ── 4 Stat cards ── */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">

                        {/* Balance */}
                        <div className="bg-white p-6 rounded-3xl shadow-sm border-l-8 border-[#7059FD] flex flex-col justify-between h-40 hover:shadow-md transition-all" data-testid="stat-wallet">
                            <div>
                                <div className="flex justify-between items-start">
                                    <span className="text-xs font-bold text-slate-400 tracking-wider uppercase">Available Balance</span>
                                    <span className="p-2 rounded-xl bg-[#7059FD]/10 text-[#7059FD]">
                                        <Wallet className="w-5 h-5" />
                                    </span>
                                </div>
                                <div className="text-3xl font-extrabold text-slate-900 mt-3">
                                    {formatINR(balance?.available_balance)}
                                </div>
                            </div>
                            <div className="flex items-center text-xs text-slate-500 mt-2">
                                <Clock className="w-4 h-4 mr-1 text-amber-500 shrink-0" />
                                Pending: <b className="text-slate-700 ml-1">{formatINR(balance?.pending_balance)}</b>
                            </div>
                        </div>

                        {/* Left Wing — teal */}
                        <div className="bg-[#10B1B6] text-white p-6 rounded-3xl shadow-sm flex flex-col justify-between h-40 relative overflow-hidden hover:shadow-md transition-all" data-testid="stat-left">
                            <div className="absolute right-0 bottom-0 translate-x-4 translate-y-4 opacity-15 pointer-events-none">
                                <ArrowDownLeft className="w-24 h-24" />
                            </div>
                            <div className="z-10">
                                <div className="flex justify-between items-center">
                                    <span className="text-xs font-bold text-white/80 tracking-wider uppercase">Left Wing</span>
                                    <span className="p-1 rounded bg-white/20">
                                        <ArrowDownLeft className="w-4 h-4 text-white" />
                                    </span>
                                </div>
                                <div className="text-4xl font-extrabold mt-3">{leftCount}</div>
                            </div>
                            <span className="z-10 text-xs text-white/90">active members</span>
                        </div>

                        {/* Right Wing — pink */}
                        <div className="bg-[#E45B80] text-white p-6 rounded-3xl shadow-sm flex flex-col justify-between h-40 relative overflow-hidden hover:shadow-md transition-all" data-testid="stat-right">
                            <div className="absolute right-0 bottom-0 translate-x-4 translate-y-4 opacity-15 pointer-events-none">
                                <ArrowDownRight className="w-24 h-24" />
                            </div>
                            <div className="z-10">
                                <div className="flex justify-between items-center">
                                    <span className="text-xs font-bold text-white/80 tracking-wider uppercase">Right Wing</span>
                                    <span className="p-1 rounded bg-white/20">
                                        <ArrowDownRight className="w-4 h-4 text-white" />
                                    </span>
                                </div>
                                <div className="text-4xl font-extrabold mt-3">{rightCount}</div>
                            </div>
                            <span className="z-10 text-xs text-white/90">active members</span>
                        </div>

                        {/* Max Pairs — blue */}
                        <div className="bg-[#2172E5] text-white p-6 rounded-3xl shadow-sm flex flex-col justify-between h-40 relative overflow-hidden hover:shadow-md transition-all" data-testid="stat-pairs">
                            <div className="absolute right-0 bottom-0 translate-x-4 translate-y-4 opacity-15 pointer-events-none">
                                <TrendingUp className="w-24 h-24" />
                            </div>
                            <div className="z-10">
                                <div className="flex justify-between items-center">
                                    <span className="text-xs font-bold text-white/80 tracking-wider uppercase">Max Pairs</span>
                                    <span className="p-1 rounded bg-white/20">
                                        <TrendingUp className="w-4 h-4 text-white" />
                                    </span>
                                </div>
                                <div className="text-4xl font-extrabold mt-3">{maxPairs}</div>
                            </div>
                            <span className="z-10 text-xs text-white/90">eligible today</span>
                        </div>
                    </div>

                    {/* ── Bonus income summary panels ── */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                        {/* Direct Referral — live stats */}
                        <div className="bg-white p-6 rounded-3xl shadow-sm hover:shadow-md transition-all">
                            <div className="flex justify-between items-start pb-4 border-b border-slate-100">
                                <div>
                                    <span className="text-xs font-bold text-slate-400 tracking-wider uppercase">Direct Referral Bonus</span>
                                    <div className="flex items-baseline gap-2 mt-1.5">
                                        <span className="font-display font-black text-2xl text-slate-900">
                                            {formatINR(directTotalNet)}
                                        </span>
                                        <span className="text-xs text-slate-500">net earned</span>
                                    </div>
                                </div>
                                <span className="px-3 py-1 bg-[#2563EB]/10 text-[#2563EB] text-xs font-bold rounded-lg whitespace-nowrap">
                                    {directIncome.length} referrals
                                </span>
                            </div>
                            <div className="mt-4 space-y-2.5">
                                <div className="flex items-center justify-between text-xs text-slate-600">
                                    <span className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-slate-400 shrink-0" />
                                        Gross per referral
                                    </span>
                                    <span className="font-bold">₹{preview ? preview.direct_bonus_gross?.toLocaleString("en-IN") : "800"}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="flex items-center gap-2 text-slate-600">
                                        <span className="w-2 h-2 rounded-full bg-orange-400 shrink-0" />Admin (15%)
                                    </span>
                                    <span className="font-semibold text-orange-600">−{formatINR(directTotalGross * 0.15)}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="flex items-center gap-2 text-slate-600">
                                        <span className="w-2 h-2 rounded-full bg-red-400 shrink-0" />TDS (5%)
                                    </span>
                                    <span className="font-semibold text-red-500">−{formatINR(directTotalGross * 0.05)}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="flex items-center gap-2 text-slate-600">
                                        <span className="w-2 h-2 rounded-full bg-blue-400 shrink-0" />Travel Fund (5%)
                                    </span>
                                    <span className="font-semibold text-blue-500">+{formatINR(directTotalGross * 0.05)}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs border-t border-slate-100 pt-2">
                                    <span className="text-slate-500">
                                        {directSettled} credited · {directPending} pending KYC/hold
                                    </span>
                                    <span className="font-extrabold text-emerald-600">{formatINR(directTotalNet)} net</span>
                                </div>
                            </div>
                        </div>

                        {/* Pairing Bonus — rates */}
                        <div className="bg-white p-6 rounded-3xl shadow-sm hover:shadow-md transition-all">
                            <div className="flex justify-between items-start pb-4 border-b border-slate-100">
                                <div>
                                    <span className="text-xs font-bold text-slate-400 tracking-wider uppercase">Pairing Bonus</span>
                                    <div className="flex items-baseline gap-2 mt-1.5">
                                        <span className="font-display font-black text-2xl text-slate-900">
                                            {formatINR(preview?.net_per_pair)}
                                        </span>
                                        <span className="text-xs text-slate-500">net per pair · nightly</span>
                                    </div>
                                </div>
                                <span className="px-3 py-1 bg-[#10B1B6]/10 text-[#10B1B6] text-xs font-bold rounded-lg whitespace-nowrap">
                                    {maxPairs} pairs today
                                </span>
                            </div>
                            <div className="mt-4 space-y-2.5">
                                <div className="flex items-center justify-between text-xs text-slate-600">
                                    <span className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-slate-400 shrink-0" />
                                        Gross per pair
                                    </span>
                                    <span className="font-bold">{formatINR(preview?.gross_per_pair)}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="flex items-center gap-2 text-slate-600">
                                        <span className="w-2 h-2 rounded-full bg-orange-400 shrink-0" />Admin ({preview?.admin_charge_pct ?? 15}%)
                                    </span>
                                    <span className="font-semibold text-orange-600">−{formatINR((preview?.gross_per_pair ?? 0) * ((preview?.admin_charge_pct ?? 15) / 100))}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="flex items-center gap-2 text-slate-600">
                                        <span className="w-2 h-2 rounded-full bg-red-400 shrink-0" />TDS ({preview?.tds_pct ?? 5}%)
                                    </span>
                                    <span className="font-semibold text-red-500">−{formatINR((preview?.gross_per_pair ?? 0) * ((preview?.tds_pct ?? 5) / 100))}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="flex items-center gap-2 text-slate-600">
                                        <span className="w-2 h-2 rounded-full bg-blue-400 shrink-0" />Travel Fund ({preview?.travel_fund_pct ?? 5}%)
                                    </span>
                                    <span className="font-semibold text-blue-500">+{formatINR((preview?.gross_per_pair ?? 0) * ((preview?.travel_fund_pct ?? 5) / 100))}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs border-t border-slate-100 pt-2">
                                    <span className="text-slate-500">Max {preview?.daily_cap ?? 10} pairs/day · runs at midnight</span>
                                    <span className="font-extrabold text-[#10B1B6]">{formatINR(preview?.net_per_pair)} net</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── My Team Income ── */}
                    {teamIncome && (
                    <div className="bg-white p-6 rounded-3xl shadow-sm">
                        {/* header */}
                        <div className="flex justify-between items-center pb-5 border-b border-slate-100">
                            <div>
                                <span className="text-xs font-bold text-slate-400 tracking-wider uppercase">Hierarchy</span>
                                <h3 className="font-display font-bold text-lg text-slate-900 mt-1">My Team Income</h3>
                            </div>
                            <Link to="/app/team" className="text-xs font-semibold text-[#2563EB] hover:underline flex items-center gap-1">
                                Full network <ChevronRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>

                        {/* 4 summary mini-cards */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
                            {[
                                { label: "Team Size",       value: teamIncome.team_size,              icon: Users,             color: "text-[#1B2B6B]", bg: "bg-[#EEF4FB]" },
                                { label: "Direct Income",   value: formatINR(teamIncome.total_direct_income),  icon: IndianRupee,       color: "text-emerald-700", bg: "bg-emerald-50" },
                                { label: "Pairing Income",  value: formatINR(teamIncome.total_pairing_income), icon: Award,             color: "text-purple-700",  bg: "bg-purple-50"  },
                                { label: "Total Earned",    value: formatINR(teamIncome.total_income),         icon: TrendingUp,        color: "text-[#2172E5]",   bg: "bg-blue-50"    },
                            ].map(({ label, value, icon: Icon, color, bg }) => (
                                <div key={label} className={`${bg} rounded-2xl p-4`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Icon className={`w-3.5 h-3.5 ${color}`} />
                                        <span className={`text-[10px] font-bold uppercase tracking-wider ${color} opacity-70`}>{label}</span>
                                    </div>
                                    <div className={`text-xl font-extrabold ${color}`}>{value}</div>
                                </div>
                            ))}
                        </div>

                        {/* Level distribution */}
                        {teamIncome.levels.length > 0 && (
                        <div className="mt-5">
                            <div className="flex items-center gap-2 mb-3">
                                <Layers className="w-3.5 h-3.5 text-slate-400" />
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Level Distribution</span>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                {teamIncome.levels.map(({ level, count }) => (
                                    <div key={level} className="flex items-center gap-1.5 bg-[#EEF4FB] px-3 py-1.5 rounded-full">
                                        <span className="text-[10px] font-bold text-slate-500 uppercase">L{level}</span>
                                        <span className="text-xs font-extrabold text-[#1B2B6B]">{count}</span>
                                        <span className="text-[10px] text-slate-400">member{count !== 1 ? "s" : ""}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        )}

                        {/* Direct referrals table */}
                        <div className="mt-5">
                            <div className="flex items-center gap-2 mb-3">
                                <UserPlus className="w-3.5 h-3.5 text-slate-400" />
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Direct Referrals (Level 1)</span>
                            </div>

                            {teamIncome.direct_referrals.length === 0 ? (
                                <div className="text-sm text-slate-400 py-6 text-center">
                                    No direct referrals yet. Share your sponsor ID to start earning.
                                </div>
                            ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse text-xs">
                                    <thead>
                                        <tr className="border-b border-slate-100">
                                            <th className="pb-2.5 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Member</th>
                                            <th className="pb-2.5 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest hidden sm:table-cell">Wing</th>
                                            <th className="pb-2.5 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest hidden md:table-cell">Joined</th>
                                            <th className="pb-2.5 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest hidden sm:table-cell">KYC</th>
                                            <th className="pb-2.5 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-right">Income</th>
                                            <th className="pb-2.5 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-center">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {teamIncome.direct_referrals.map((m) => (
                                            <tr key={m.code} className="hover:bg-slate-50/60 transition-colors">
                                                {/* Member */}
                                                <td className="py-3 pr-3 align-middle">
                                                    <div className="font-semibold text-slate-800 text-xs truncate max-w-[120px]">{m.name}</div>
                                                    <div className="text-[10px] font-mono text-slate-400">{m.code}</div>
                                                    {m.sub_team_size > 0 && (
                                                        <div className="text-[9px] text-blue-500 font-semibold mt-0.5">+{m.sub_team_size} sub-member{m.sub_team_size !== 1 ? "s" : ""}</div>
                                                    )}
                                                </td>
                                                {/* Wing */}
                                                <td className="py-3 pr-3 align-middle hidden sm:table-cell">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-bold ${
                                                        m.side === "L"
                                                            ? "bg-teal-50 text-teal-700"
                                                            : m.side === "R"
                                                            ? "bg-pink-50 text-pink-700"
                                                            : "bg-slate-50 text-slate-500"
                                                    }`}>
                                                        {m.side === "L" ? "Left" : m.side === "R" ? "Right" : "—"}
                                                    </span>
                                                </td>
                                                {/* Joined */}
                                                <td className="py-3 pr-3 text-slate-400 whitespace-nowrap align-middle hidden md:table-cell">
                                                    {m.joined_at
                                                        ? new Date(m.joined_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                                                        : "—"}
                                                </td>
                                                {/* KYC */}
                                                <td className="py-3 pr-3 align-middle hidden sm:table-cell">
                                                    {m.kyc_status === "approved" ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold">
                                                            <CheckCircle2 className="w-2.5 h-2.5" /> Verified
                                                        </span>
                                                    ) : m.kyc_status === "pending" ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[10px] font-bold">
                                                            <Clock className="w-2.5 h-2.5" /> Pending
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-50 text-slate-400 text-[10px] font-bold">
                                                            <AlertCircle className="w-2.5 h-2.5" /> None
                                                        </span>
                                                    )}
                                                </td>
                                                {/* Income */}
                                                <td className="py-3 pr-3 text-right font-extrabold align-middle whitespace-nowrap">
                                                    {m.bonus_net > 0 ? (
                                                        <span className="text-emerald-600 font-mono text-xs">+{formatINR(m.bonus_net)}</span>
                                                    ) : (
                                                        <span className="text-slate-300 font-mono text-xs">₹0</span>
                                                    )}
                                                </td>
                                                {/* Bonus status */}
                                                <td className="py-3 text-center align-middle">
                                                    {m.bonus_net === 0 ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-50 text-slate-400 text-[10px] font-bold whitespace-nowrap">
                                                            No bonus
                                                        </span>
                                                    ) : m.kyc_hold ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[10px] font-bold whitespace-nowrap">
                                                            <AlertCircle className="w-2.5 h-2.5" /> KYC Hold
                                                        </span>
                                                    ) : m.bonus_settled ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold whitespace-nowrap">
                                                            <CheckCircle2 className="w-2.5 h-2.5" /> Credited
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[10px] font-bold whitespace-nowrap">
                                                            <Clock className="w-2.5 h-2.5" /> Pending
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="border-t-2 border-slate-200">
                                            <td colSpan={4} className="pt-3 text-xs font-semibold text-slate-500">
                                                {teamIncome.direct_referrals_count} direct referral{teamIncome.direct_referrals_count !== 1 ? "s" : ""} · {teamIncome.team_size} total team
                                            </td>
                                            <td className="pt-3 text-right font-extrabold text-emerald-600 font-mono text-sm whitespace-nowrap">
                                                +{formatINR(teamIncome.total_direct_income)}
                                            </td>
                                            <td />
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                            )}
                        </div>
                    </div>
                    )}

                    {/* ── Recent Earnings table ── */}
                    <div className="bg-white p-6 rounded-3xl shadow-sm" data-testid="recent-ledger">
                        <div className="flex justify-between items-center mb-5">
                            <div>
                                <span className="text-xs font-bold text-slate-400 tracking-wider uppercase">Recent Earnings</span>
                                <h3 className="font-display font-bold text-lg text-slate-900 mt-1">Wallet Activity</h3>
                            </div>
                            <Link to="/app/wallet" className="text-xs font-semibold text-[#2563EB] hover:underline flex items-center gap-1">
                                View all <ChevronRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>

                        {(() => {
                            const bonusLedger = ledger.filter(l => BONUS_TYPES.has(l.type));
                            return bonusLedger.length === 0 ? (
                            <div className="text-sm text-slate-500 py-8 text-center">
                                No bonus activity yet. Add your first member to start earning.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse text-xs">
                                    <thead>
                                        <tr className="border-b border-slate-100">
                                            <th className="pb-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Activity Type</th>
                                            <th className="pb-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest hidden sm:table-cell">Date</th>
                                            <th className="pb-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-right whitespace-nowrap">Gross</th>
                                            <th className="pb-3 text-[10px] font-extrabold text-orange-400 uppercase tracking-widest text-right whitespace-nowrap hidden md:table-cell">Admin</th>
                                            <th className="pb-3 text-[10px] font-extrabold text-red-400 uppercase tracking-widest text-right whitespace-nowrap hidden md:table-cell">TDS</th>
                                            <th className="pb-3 text-[10px] font-extrabold text-blue-400 uppercase tracking-widest text-right whitespace-nowrap hidden md:table-cell">Fund</th>
                                            <th className="pb-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-right whitespace-nowrap">Net</th>
                                            <th className="pb-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-center whitespace-nowrap">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {bonusLedger.map((l) => {
                                            const badge = TYPE_BADGE[l.type] ?? { label: l.type, cls: "bg-slate-100 text-slate-600" };
                                            const bk = getBonusBreakdown(l);
                                            const now = new Date();
                                            const settleDate = l.settle_after ? new Date(l.settle_after) : null;
                                            const kycHold = settleDate && settleDate.getFullYear() >= 2099;
                                            const isPending = !kycHold && settleDate && settleDate > now;

                                            return (
                                                <tr key={l.id} className="hover:bg-slate-50/50 transition-colors">
                                                    {/* Activity Type */}
                                                    <td className="py-3.5 pr-3 align-middle">
                                                        <span className={`inline-flex items-center px-2.5 py-1 text-[10px] font-bold rounded-lg uppercase whitespace-nowrap ${badge.cls}`}>
                                                            {badge.label}
                                                        </span>
                                                    </td>
                                                    {/* Date */}
                                                    <td className="py-3.5 pr-3 text-slate-400 font-medium whitespace-nowrap align-middle hidden sm:table-cell">
                                                        {new Date(l.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                                                    </td>
                                                    {/* Gross */}
                                                    <td className="py-3.5 pr-3 text-right font-mono text-slate-600 whitespace-nowrap align-middle">
                                                        {bk ? formatINR(bk.gross) : <span className="text-slate-200">—</span>}
                                                    </td>
                                                    {/* Admin */}
                                                    <td className="py-3.5 pr-3 text-right font-mono whitespace-nowrap align-middle hidden md:table-cell">
                                                        {bk ? <span className="text-orange-500">−{formatINR(bk.admin)}</span> : <span className="text-slate-200">—</span>}
                                                    </td>
                                                    {/* TDS */}
                                                    <td className="py-3.5 pr-3 text-right font-mono whitespace-nowrap align-middle hidden md:table-cell">
                                                        {bk ? <span className="text-red-500">−{formatINR(bk.tds)}</span> : <span className="text-slate-200">—</span>}
                                                    </td>
                                                    {/* Fund */}
                                                    <td className="py-3.5 pr-3 text-right font-mono whitespace-nowrap align-middle hidden md:table-cell">
                                                        {bk ? <span className="text-blue-500">+{formatINR(bk.fund)}</span> : <span className="text-slate-200">—</span>}
                                                    </td>
                                                    {/* Net */}
                                                    <td className={`py-3.5 pr-3 text-right font-extrabold text-sm whitespace-nowrap align-middle ${l.amount >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                                                        {l.amount >= 0 ? "+" : ""}{formatINR(l.amount)}
                                                    </td>
                                                    {/* Status */}
                                                    <td className="py-3.5 text-center align-middle">
                                                        {kycHold ? (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[10px] font-bold whitespace-nowrap">
                                                                <AlertCircle className="w-3 h-3" /> KYC Hold
                                                            </span>
                                                        ) : isPending ? (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[10px] font-bold whitespace-nowrap">
                                                                <Clock className="w-3 h-3" /> Pending
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold whitespace-nowrap">
                                                                <CheckCircle2 className="w-3 h-3" /> Credited
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        );
                        })()}
                    </div>
                </div>

                {/* ─────────────── Right sidebar panel ─────────────── */}
                <div className="w-72 hidden xl:flex flex-col gap-5 flex-shrink-0">

                    {/* Wing symmetry meter */}
                    <div className="bg-white p-6 rounded-3xl shadow-sm">
                        <div className="mb-5">
                            <span className="text-xs font-bold text-slate-400 tracking-wider uppercase">Payout Target</span>
                            <h3 className="font-display font-bold text-lg text-slate-900 mt-1">Network Balance</h3>
                        </div>

                        <div className="p-4 bg-[#EEF4FB] rounded-2xl border border-blue-100">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold text-slate-500">Wing symmetry</span>
                                <span className="text-xs font-bold text-[#2563EB]">{symmetryPct}% matched</span>
                            </div>
                            <div className="w-full bg-slate-200 h-3 rounded-full overflow-hidden">
                                <div
                                    className="bg-[#2563EB] h-full rounded-full transition-all duration-500"
                                    style={{ width: `${symmetryPct}%` }}
                                />
                            </div>
                            <p className="text-[11px] text-slate-400 mt-2">
                                {Math.min(leftCount, rightCount)} matching pairs of {Math.max(leftCount, rightCount)} max potential channels.
                            </p>
                        </div>

                        <div className="mt-5 space-y-4">
                            <h4 className="text-xs font-bold text-slate-400 tracking-widest uppercase">Team Leader Channels</h4>
                            <div className="space-y-2">
                                <div className="flex justify-between items-center p-3 bg-teal-50 rounded-xl">
                                    <span className="text-xs font-bold text-teal-800">Left Wing Core</span>
                                    <span className="font-extrabold text-teal-700">{leftCount}</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-pink-50 rounded-xl">
                                    <span className="text-xs font-bold text-pink-800">Right Wing Growth</span>
                                    <span className="font-extrabold text-pink-700">{rightCount}</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-xl">
                                    <span className="text-xs font-bold text-purple-800">Max Eligible Pairs</span>
                                    <span className="font-extrabold text-purple-700">{maxPairs}</span>
                                </div>
                            </div>
                        </div>

                        <div className="mt-5 pt-5 border-t border-slate-100">
                            <button
                                onClick={copyId}
                                className="w-full py-3 bg-slate-50 hover:bg-slate-100 text-xs font-bold rounded-2xl transition-all border border-slate-100 flex items-center justify-center gap-2"
                            >
                                {copied
                                    ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                    : <Copy className="w-4 h-4 text-slate-500" />
                                }
                                {copied ? "Copied!" : "+ Share Sponsor ID"}
                            </button>
                        </div>
                    </div>

                    {/* Quick actions */}
                    <div className="bg-white p-6 rounded-3xl shadow-sm">
                        <h4 className="text-xs font-bold text-slate-400 tracking-widest uppercase mb-4">Quick Actions</h4>
                        <div className="space-y-2">
                            {[
                                { to: "/app/add-member", label: "Add New Member",  icon: UserPlus  },
                                { to: "/app/pairing",    label: "Pairing Bonus",   icon: Zap       },
                                { to: "/app/team",       label: "My Network",      icon: Users     },
                                { to: "/app/wallet",     label: "Wallet",          icon: Wallet    },
                            ].map(({ to, label, icon: Icon }) => (
                                <Link
                                    key={to}
                                    to={to}
                                    className="flex items-center justify-between p-3 bg-[#EEF4FB] hover:bg-blue-50 rounded-xl transition-colors group"
                                >
                                    <div className="flex items-center gap-2.5">
                                        <Icon className="w-4 h-4 text-[#2563EB]" />
                                        <span className="text-xs font-bold text-[#1B2B6B]">{label}</span>
                                    </div>
                                    <ChevronRight className="w-3.5 h-3.5 text-[#2563EB] opacity-60 group-hover:opacity-100 transition-opacity" />
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>

            </div>
        </AppShell>
    );
}
